import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { parse } from 'query-string';

type HashDataObject = {
  category: string | undefined;
  engraving: string | undefined;
  features: string | undefined;
  length: string | undefined;
  position: string | undefined;
  printProductId: string | undefined;
  query: string | undefined;
  searchPage: string | undefined;
  variationId: string | undefined;
  width: string | undefined;
};

export const getSku = (sku: string | undefined, isPreselectDimension: boolean): string | null => {
  if (!sku) return null;
  const parts = sku?.split('-');
  const isLegacySku = parts?.length > 2;
  const selectedLegacySku =
    isPreselectDimension && isLegacySku ? `${parts[0]}-${parts[1]}` : parts[0];
  return isLegacySku ? selectedLegacySku : sku;
};

export const getHashParameters = (
  locationHash: string | undefined,
  locationSearch: string | undefined = undefined,
): HashDataObject => {
  const hashObject = parse(locationHash) || {};
  const variationIdFromUrl = hashObject.sku as string | undefined;
  const categoryFromHash = hashObject['nav-c'] as string | undefined;
  const query = hashObject['nav-q'] as string | undefined;
  const searchPage = hashObject['nav-p'] as string | undefined;
  const position = hashObject['nav-i'] as string | undefined;
  const features = hashObject.features as string | undefined;
  const isPreselectDimension = hashObject['nav-pd'] as string | undefined;
  const lengthFromHash = hashObject.length as string | undefined;
  const widthFromHash = hashObject.width as string | undefined;
  const engravingFromHash = hashObject.engraving as string | undefined;

  /** sanitize variationId for use with "old" three-part sku */
  const variationId = getSku(variationIdFromUrl, isPreselectDimension === 'true');
  /** remove '@...' from category */

  const printProductId = hashObject.pp as string | undefined;

  const searchObject = parse(locationSearch) || {};
  const isPreselectDimensionFromUrl =
    (searchObject['nav-pd'] as string | undefined) || isPreselectDimension;
  const searchVariationId = getSku(
    searchObject.sku as string | undefined,
    isPreselectDimensionFromUrl === 'true',
  );
  const categoryFromUrl = (searchObject['nav-c'] as string | undefined) || categoryFromHash;
  const [category] = categoryFromUrl ? categoryFromUrl?.split('@') : [null];
  const length = (searchObject.length as string | undefined) || lengthFromHash;
  const width = (searchObject.width as string | undefined) || widthFromHash;
  const engravingEncoded = (searchObject.engraving as string | undefined) || engravingFromHash;
  const engraving =
    typeof engravingEncoded !== 'undefined' ? decodeURIComponent(engravingEncoded) : undefined;

  return {
    category,
    engraving,
    features,
    length,
    position,
    printProductId,
    query,
    searchPage,
    variationId: searchVariationId || variationId,
    width,
  };
};

const useHashParameters = (): HashDataObject => {
  const { hash, search } = useLocation();

  return useMemo(() => getHashParameters(hash, search), [hash]);
};

export default useHashParameters;
